import Dropdown, {Anchor} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Loader from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import {differenceInSeconds, parseISO} from 'date-fns'
import {useCallback, useMemo, useState} from 'react'

import EntityLink from '../../../containers/EntityLink'
import useInterval from '../../../hooks/useInterval'
import useTimeSelector from '../../../hooks/useTimeSelector'
import {getBuild} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {BuildPageTabNamesEnum, stringifyId} from '../../../types'
import {formatDuration} from '../../../utils/dateTimeUtils'
import {emptyArray} from '../../../utils/empty'
import Popup from '../Popup/Popup.lazy'
import RouterLink from '../RouterLink/RouterLink'
import SvgIcon from '../SvgIcon/SvgIcon'

import {WaitReasons} from './BuildQueueInfo.consts'
import {getWaitReasonsArg} from './BuildQueueInfo.rest'
import type {Props, WaitReason} from './BuildQueueInfo.types'

import styles from './BuildQueueInfo.css'

import {useAppSelector} from 'src/hooks/react-redux'

const MILLISECONDS_IN_SECOND = 1000
const INTERVAL = 5000

function BuildQueueInfo({
  className,
  buildId,
  inline,
  queuedDate,
  startDate,
  currentWaitReason,
  aside,
  queued,
  titleClassName,
  reasonsClassName,
  dependenciesRange,
  highlightText = true,
  withoutTitle,
  groupOwnReasons,
  withIcons,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const onShow = useCallback(() => setIsOpen(true), [])
  const onHide = useCallback(() => setIsOpen(false), [])

  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  const startDateObj = startDate != null ? parseISO(startDate) : null
  const startOrNow = useTimeSelector(now => Number(startDateObj ?? now))

  const {properties, waitReasonsReady, refetch} = restApi.endpoints.getBuild.useQuery(
    getWaitReasonsArg(buildId),
    {
      selectFromResult: ({data}) => ({
        properties: data?.queuedWaitReasons?.property ?? emptyArray,
        waitReasonsReady: data != null,
      }),
    },
  )
  useInterval(refetch, (isOpen || inline) && queued ? INTERVAL : null)
  const waitReasons = useMemo(
    () =>
      properties.map(({name = '', value}) => ({
        reason: name,
        durationInSeconds: Number(value) / MILLISECONDS_IN_SECOND,
      })),
    [properties],
  )

  if (queuedDate == null) {
    return null
  }

  const timeInQueue = differenceInSeconds(startOrNow, parseISO(queuedDate))

  const useDropdown = inline !== true && waitReasons.length > 0
  const titleClasses = classNames(titleClassName, styles.title, {
    [styles.inlineTitle]: inline === true && highlightText === true,
  })
  const title = (
    <span className={titleClasses} data-test-queue-info>
      {withIcons && (
        <span className={styles.iconsOffset}>
          <SvgIcon icon="hourglass" className={styles.queuedIcon} />
        </span>
      )}
      {!withoutTitle && 'Queue time '}
      {formatDuration(timeInQueue)}
    </span>
  )

  const other = waitReasons.reduce(
    (acc, {durationInSeconds}) => acc - durationInSeconds,
    timeInQueue,
  )

  const allReasons =
    other >= 1 && !queued
      ? [
          ...waitReasons,
          {
            reason: 'Other',
            durationInSeconds: other,
          },
        ]
      : waitReasons

  const formatReason = ({reason, durationInSeconds}: WaitReason) => {
    const isWaitForDependencies = reason === WaitReasons.DEPENDENCY_NOT_READY
    const isCurrentReason = currentWaitReason === reason

    const duration = isCurrentReason ? durationInSeconds + other : durationInSeconds
    const formatedDuration = formatDuration(duration)
    return (
      <div key={reason} className={styles.reason}>
        {withIcons && <span className={styles.iconsOffset} />}
        {!isWaitForDependencies ? (
          <span
            className={classNames({
              [styles.reasonDuration]: highlightText === true,
            })}
          >
            {formatedDuration}
          </span>
        ) : dependenciesRange != null ? (
          <RouterLink params={params => ({...params, range: dependenciesRange})}>
            {formatedDuration}
          </RouterLink>
        ) : (
          <EntityLink
            buildId={buildId}
            buildTypeId={buildTypeId}
            params={{
              buildTab: stringifyId(BuildPageTabNamesEnum.DEPENDENCIES),
              mode: 'timeline',
            }}
          >
            {formatedDuration}
          </EntityLink>
        )}
        {`: ${reason}`}
      </div>
    )
  }
  const dependencyReason = allReasons.find(
    ({reason}) => reason === WaitReasons.DEPENDENCY_NOT_READY,
  )
  const reasons =
    groupOwnReasons && dependencyReason != null ? (
      <>
        {formatReason(dependencyReason)}
        <div
          className={classNames(styles.reason, {
            [styles.inlineTitle]: highlightText === true,
          })}
        >
          {withIcons && (
            <span className={styles.iconsOffset}>
              <span className={styles.ownQueueTimeIcon} />
            </span>
          )}
          {'Own queue time '}
          {formatDuration(timeInQueue - dependencyReason.durationInSeconds)}
        </div>
        {allReasons
          .filter(({reason}) => reason !== WaitReasons.DEPENDENCY_NOT_READY)
          .map(formatReason)}
      </>
    ) : (
      allReasons.map(formatReason)
    )

  if (useDropdown) {
    return (
      <div className={classNames(className, styles.wrapper)}>
        <Dropdown
          className={styles.dropdown}
          anchor={<Anchor className={styles.anchor}>{title}</Anchor>}
          onShow={onShow}
          onHide={onHide}
        >
          <Popup className={styles.popup}>{reasons}</Popup>
        </Dropdown>
      </div>
    )
  }

  return (
    <div className={classNames(className, styles.wrapper)}>
      {title}
      {inline === true && aside}
      {(waitReasons.length > 0 || (inline === true && !waitReasonsReady)) && (
        <div className={classNames(reasonsClassName, styles.inlineReasons)}>
          {waitReasons.length > 0 ? reasons : <Loader>{'Loading reasons...'}</Loader>}
        </div>
      )}
    </div>
  )
}

export default BuildQueueInfo
